// buildPaletteActions — the single source of truth for the Ctrl+K command
// palette's items. Kept a pure function (deps in → CommandAction[] out) so it is
// unit-testable without rendering AppShell (which pulls in MSAL/router/stores).
// AppShell wires the deps; CommandPalette renders + filters the result.

import { groupJobsByConversation } from "@/lib/groupJobsByConversation";
import { deriveDisplayLabel } from "@/lib/jobStatusBadge";
import {
  DEFAULT_TEXT_SCALE,
  MAX_TEXT_SCALE,
  MIN_TEXT_SCALE,
  type MotionPreference,
  type ThemePreference,
} from "@/lib/settings";
import { shortcut } from "@/lib/shortcuts";
import type { JobSnapshot } from "@/types";

import type { CommandAction } from "./CommandPalette";

export interface PaletteDeps {
  onFocusComposer: () => void;
  onNewThread: () => void;
  onOpenSettings: () => void;
  onScrollToBottom: () => void;
  onSetTheme: (theme: ThemePreference) => void;
  onSetMotion: (motion: MotionPreference) => void;
  onToggleSidebar: () => void;
  onSignOut: () => void;
  onSetTextScale: (scale: number) => void;
  textScale: number;
  tenants: string[];
  onSwitchTenant: (tenantId: string) => void;
  recentJobs: JobSnapshot[];
  onOpenJob: (job: JobSnapshot, conversationJobs?: readonly JobSnapshot[]) => void;
}

const TEXT_SCALE_STEP = 0.125;

function clampTextScale(value: number): number {
  return Math.min(MAX_TEXT_SCALE, Math.max(MIN_TEXT_SCALE, value));
}

// Group emission order is load-bearing: CommandPalette's grouped() renders
// groups in first-seen order, so this sequence (Quick actions → Navigation →
// Recent threads → Switch tenant → Appearance → Account) IS the palette layout.
export function buildPaletteActions(deps: PaletteDeps): CommandAction[] {
  const actions: CommandAction[] = [
    {
      id: "focus-composer",
      group: "Quick actions",
      label: "Focus composer",
      description: "Move keyboard focus to the text input",
      onActivate: deps.onFocusComposer,
    },
    {
      id: "new-thread",
      group: "Quick actions",
      label: "New thread",
      description: "Clear current thread and start fresh",
      shortcut: shortcut("N"),
      onActivate: deps.onNewThread,
    },
    {
      id: "open-settings",
      group: "Quick actions",
      label: "Open settings",
      description: "Theme, motion, shortcuts",
      onActivate: deps.onOpenSettings,
    },
    {
      id: "toggle-sidebar",
      group: "Quick actions",
      label: "Toggle sidebar",
      description: "Collapse or expand the sidebar",
      onActivate: deps.onToggleSidebar,
    },
    {
      id: "scroll-to-bottom",
      group: "Navigation",
      label: "Scroll to bottom of thread",
      onActivate: deps.onScrollToBottom,
    },
  ];

  // Grouped by CONVERSATION, matching the sidebar (commit 9108d0c4). Listing one
  // entry per job made a 3-turn conversation appear as three separate threads —
  // the same defect the operator reported in the sidebar, and it would have
  // survived here. Title from the FIRST turn (the subject; a follow-up like "ok,
  // tell me more about it" is a useless label), and hand over EVERY turn so the
  // palette restores the whole thread rather than only its last exchange — the
  // sidebar's defect, which would otherwise have survived here too. Both halves
  // are pinned by PAL-CONV-1.
  for (const group of groupJobsByConversation(deps.recentJobs)) {
    actions.push({
      id: `open-job-${group.key}`,
      group: "Recent threads",
      label: deriveDisplayLabel(group.firstJob),
      onActivate: () => deps.onOpenJob(group.latestJob, group.jobs),
    });
  }

  if (deps.tenants.length >= 2) {
    for (const tenantId of deps.tenants) {
      actions.push({
        id: `switch-tenant-${tenantId}`,
        group: "Switch tenant",
        label: `Switch to ${tenantId}`,
        onActivate: () => deps.onSwitchTenant(tenantId),
      });
    }
  }

  actions.push(
    {
      id: "theme-auto",
      group: "Appearance",
      label: "Theme: Auto",
      description: "Follow system preference",
      onActivate: () => deps.onSetTheme("auto"),
    },
    {
      id: "theme-light",
      group: "Appearance",
      label: "Theme: Light",
      onActivate: () => deps.onSetTheme("light"),
    },
    {
      id: "theme-dark",
      group: "Appearance",
      label: "Theme: Dark",
      onActivate: () => deps.onSetTheme("dark"),
    },
    {
      id: "motion-auto",
      group: "Appearance",
      label: "Motion: Auto",
      description: "Follow system preference",
      onActivate: () => deps.onSetMotion("auto"),
    },
    {
      id: "motion-reduce",
      group: "Appearance",
      label: "Motion: Reduce",
      description: "Disable animations + transitions",
      onActivate: () => deps.onSetMotion("reduce"),
    },
    {
      id: "text-size-increase",
      group: "Appearance",
      label: "Text size: Increase",
      onActivate: () => deps.onSetTextScale(clampTextScale(deps.textScale + TEXT_SCALE_STEP)),
    },
    {
      id: "text-size-decrease",
      group: "Appearance",
      label: "Text size: Decrease",
      onActivate: () => deps.onSetTextScale(clampTextScale(deps.textScale - TEXT_SCALE_STEP)),
    },
    {
      id: "text-size-reset",
      group: "Appearance",
      label: "Text size: Reset",
      onActivate: () => deps.onSetTextScale(DEFAULT_TEXT_SCALE),
    },
    {
      id: "sign-out",
      group: "Account",
      label: "Sign out",
      description: "End your session",
      onActivate: deps.onSignOut,
    },
  );

  return actions;
}
